import { useCallback, useMemo, useState } from 'react';
import { useDebounce } from 'react-use';
import { createContext, useContext } from 'use-context-selector';

import { IFormCreationValues, IStationAssignFormValues } from './useForm';

import { useGetDetailVenue } from '~hooks/useGetDetailVenue';
import { useGetDistributionChannelOptions } from '~hooks/useGetDistributionChannelOptions';
import { PINNING_STEP, usePinningStepContext } from '~providers';
import { DistributionChannel, ICoordinate, VenueDetail } from '~types';

export const LOCAL_FORM_VALUES = 'localFormValues';
export const LOCAL_PINNING_STEP = 'localPinningStep';
export const LOCAL_PLACEMENT_LIST = 'localPlacementList';
export const LOCAL_VENUE_ID = 'localVenueId';
export const LOCAL_GOOGLE_PLACE_ID = 'localGooglePlaceId';
export const LOCAL_IS_RELOCATION_FLOW = 'localIsRelocationFlow';
export const LOCAL_CONTACT_SKIPPED_STEP = 'localContactSkippedStep';

const initialContextValues = {
  /* A value list record both placement create and drop requests */
  placementListInfo: [] as IStationAssignFormValues[],
  handleAddNewPlacementInfo: (_: IStationAssignFormValues) => {
    return;
  },
  handleSetPlacementList: (_: IStationAssignFormValues[]) => {
    return;
  },

  distributionChannelOptions: [] as DistributionChannel[],
  existingVenueDetail: null as VenueDetail | null,
  existingVenueCoordinates: null as ICoordinate | null,

  handleClearLocal: () => {
    return;
  },
  selectedPlacement: 0,
  handleSelectPlacement: (_: number) => {
    return;
  },
  handleRemovePlacement: (_: number) => {
    return;
  },
  handleReset: () => {
    return;
  },
  handleUpdateLastPlacement: (_: IStationAssignFormValues) => {
    return;
  },
  handleUpdatePinningFormData: (_value: IFormCreationValues) => {
    return;
  },
};

const VenuePlacementCreationContext = createContext(initialContextValues);

type Props = {
  children: React.ReactNode;
};

export const VenuePlacementCreationProvider: React.FC<Props> = ({
  children,
}) => {
  const {
    selectedVenueId,
    handleSetSelectedVenueId,
    isUpdating,
    setIsUpdating,
    pinningStep,
    handleSetSelectedPlacementId,
    handleSetContactSkippedStep,
  } = usePinningStepContext();
  const { venueDetailData, venueCoordination } =
    useGetDetailVenue(selectedVenueId);

  const [placementListInfo, setPlacementListInfo] = useState<
    IStationAssignFormValues[]
  >([]);

  const handleSetPlacementList = useCallback(
    (values: IStationAssignFormValues[]) => {
      setPlacementListInfo(values);
    },
    [setPlacementListInfo],
  );

  const handleAddNewPlacementInfo = useCallback(
    (values: IStationAssignFormValues) => {
      setPlacementListInfo((prev) => [...prev, values]);
      handleSetSelectedPlacementId(undefined);
    },
    [handleSetSelectedPlacementId],
  );

  const [selectedPlacement, setSelectedPlacement] = useState<number>(0);
  const handleSelectPlacement = useCallback(
    (index: number) => {
      setSelectedPlacement(index);
    },
    [setSelectedPlacement],
  );

  const handleClearLocal = useCallback(() => {
    localStorage.removeItem(LOCAL_PINNING_STEP);
    localStorage.removeItem(LOCAL_VENUE_ID);
    localStorage.removeItem(LOCAL_FORM_VALUES);
    localStorage.removeItem(LOCAL_PLACEMENT_LIST);
    localStorage.removeItem(LOCAL_GOOGLE_PLACE_ID);
    localStorage.removeItem(LOCAL_IS_RELOCATION_FLOW);
    localStorage.removeItem(LOCAL_CONTACT_SKIPPED_STEP);
  }, []);
  const { distributionChannelOptions } = useGetDistributionChannelOptions();

  // Reset all form values
  const handleReset = useCallback(() => {
    setPlacementListInfo([]);
    setSelectedPlacement(0);
    handleSetSelectedVenueId(undefined);
    setIsUpdating(false);
    handleSetContactSkippedStep(undefined);
    handleClearLocal();
  }, [
    handleClearLocal,
    handleSetContactSkippedStep,
    handleSetSelectedVenueId,
    setIsUpdating,
  ]);

  const handleRemovePlacement = useCallback(
    (index: number) => {
      setPlacementListInfo((prev) => [...prev.filter((_, i) => i !== index)]);
    },
    [setPlacementListInfo],
  );

  const handleUpdateLastPlacement = useCallback(
    (values: IStationAssignFormValues) => {
      if (placementListInfo.length === 0) return;
      setPlacementListInfo((prev) => {
        const newPlacementList = [...prev];
        newPlacementList[newPlacementList.length - 1] = values;
        return newPlacementList;
      });
    },
    [placementListInfo.length],
  );

  const handleUpdatePinningFormData = useCallback(
    (values: IFormCreationValues) => {
      /**
       * Update the latest placement if step as updating else add new one to the placement list
       */
      if (isUpdating && placementListInfo.length > 0) {
        handleUpdateLastPlacement(values.placement);
        setIsUpdating(false);
      } else {
        handleAddNewPlacementInfo(values.placement);
      }
    },
    [
      handleAddNewPlacementInfo,
      handleUpdateLastPlacement,
      isUpdating,
      placementListInfo.length,
      setIsUpdating,
    ],
  );

  useDebounce(
    () => {
      if (pinningStep !== PINNING_STEP.START)
        localStorage.setItem(
          LOCAL_PLACEMENT_LIST,
          JSON.stringify(placementListInfo), // Save placementListInfo to localStorage whenever they change
        );
    },
    500, // 500ms delay debounce to improve performace
    [placementListInfo],
  );

  const providerValues = useMemo(
    () => ({
      placementListInfo,
      handleAddNewPlacementInfo,
      handleSetPlacementList,

      distributionChannelOptions,
      existingVenueDetail: selectedVenueId
        ? venueDetailData.data || null
        : null,
      existingVenueCoordinates: selectedVenueId ? venueCoordination : null,
      handleClearLocal,
      selectedPlacement,
      handleSelectPlacement,
      handleRemovePlacement,
      handleReset,
      handleUpdateLastPlacement,
      handleUpdatePinningFormData,
    }),
    [
      placementListInfo,
      handleAddNewPlacementInfo,
      handleSetPlacementList,
      distributionChannelOptions,
      selectedVenueId,
      venueDetailData.data,
      venueCoordination,
      handleClearLocal,
      selectedPlacement,
      handleSelectPlacement,
      handleRemovePlacement,
      handleReset,
      handleUpdateLastPlacement,
      handleUpdatePinningFormData,
    ],
  );
  return (
    <VenuePlacementCreationContext.Provider value={providerValues}>
      {children}
    </VenuePlacementCreationContext.Provider>
  );
};

export const useVenuePlacementCreationContext = () =>
  useContext(VenuePlacementCreationContext);
